import api from "../../../../../api/axios";

// PAD Info API. Uses the shared authed axios instance.
// Backend lives in Server/src/routes/pad-info, mounted at /v2/pad-info, and the
// notification center at /v2/notifications.
const BASE = "v2/pad-info";
const NOTIF = "v2/notifications";

// ---- entries -----------------------------------------------------------------
export const listPadEntries = (mastersheetItemId, includeArchived = false) =>
  api.get(`${BASE}/entries`, { params: { mastersheet_item_id: mastersheetItemId, ...(includeArchived ? { include_archived: 1 } : {}) } });

export const listArchivedPadEntries = (archivedItemId) => api.get(`${BASE}/entries/archived/${archivedItemId}`);

export const createPadEntry = (payload) => api.post(`${BASE}/entries`, payload);

export const updatePadEntry = (id, payload) => api.patch(`${BASE}/entries/${id}`, payload);

export const voidPadEntry = (id, reason) => api.post(`${BASE}/entries/${id}/void`, { reason });

export const verifyPadEntry = (id, { verified = true, note = "" } = {}) =>
  api.post(`${BASE}/entries/${id}/verify`, { verified, note });

// ---- settings ----------------------------------------------------------------
// One company-wide value today: the delay approval day. Anyone who can open the modal
// reads it; only the company writes it (server-enforced).
export const getPadSettings = () => api.get(`${BASE}/settings`);

export const updatePadSettings = (payload) => api.put(`${BASE}/settings`, payload);

export const getUnitPadStatus = (mastersheetItemId) =>
  api.get(`${BASE}/unit-status`, { params: { mastersheet_item_id: mastersheetItemId } });

// ---- attachments -------------------------------------------------------------
// Two phases, the same shape the Documents section uses: the file goes to storage through
// the shared upload route, then its key is recorded against the PAD entry.

/** Push one file to storage and hand back { url, key }. */
export const uploadPadAttachmentFile = async (file, onUploadProgress) => {
  const formData = new FormData();
  formData.append("documents", file);
  const { data } = await api.post("v2/master_sheet/documents/upload", formData, { onUploadProgress });
  const first = Array.isArray(data?.data) ? data.data[0] : null;
  if (!first?.key) throw new Error("Upload did not return a storage key");
  return first;
};

/** Record an already-uploaded file against a PAD entry. */
export const addPadAttachment = (entryId, { key, filename, size, mime }) =>
  api.post(`${BASE}/entries/${entryId}/attachments`, { key, filename, size, mime });

/** Files the unit's signed lease addendum on the entry, straight from where it is stored. */
export const addPadAddendumAttachment = (entryId, leaseAddendumId) =>
  api.post(`${BASE}/entries/${entryId}/attachments`, { lease_addendum_id: leaseAddendumId });

/** A short-lived link for viewing / downloading / printing one attachment. */
export const getPadAttachmentUrl = (entryId, attachmentId) =>
  api.get(`${BASE}/entries/${entryId}/attachments/${attachmentId}/url`);

export const removePadAttachment = (entryId, attachmentId) =>
  api.delete(`${BASE}/entries/${entryId}/attachments/${attachmentId}`);


// ---- report ------------------------------------------------------------------
// `unverifiedOnly` filters every section to rows accounting has not signed off — the same
// flag rides the Excel export so the screen and the workbook can never disagree.
const reportParams = (month, unverifiedOnly) => ({
  ...(month ? { month } : {}),
  ...(unverifiedOnly ? { unverified: "1" } : {}),
});

export const getPadInfoReport = async (mastersheetId, month, unverifiedOnly = false) => {
  if (!mastersheetId) throw new Error("mastersheetId is required");
  const { data } = await api.get(`${BASE}/report/${mastersheetId}`, { params: reportParams(month, unverifiedOnly) });
  return data;
};

/** "The month's PAD work is done" — the server emails accounting with the Excel attached. */
export const notifyPadAccounting = (mastersheetId, month) =>
  api.post(`${BASE}/report/${mastersheetId}/notify-accounting`, { month });

export const exportPadInfoReportExcel = async (mastersheetId, month, unverifiedOnly = false) => {
  if (!mastersheetId) throw new Error("mastersheetId is required");
  const response = await api.get(`${BASE}/report/${mastersheetId}/export-excel`, {
    params: reportParams(month, unverifiedOnly),
    responseType: "blob",
  });
  return response.data;
};

// ---- notification center -----------------------------------------------------
export const getPadPending = (fresh = false) => api.get(`${BASE}/pending`, { params: fresh ? { fresh: 1 } : {} });

export const getNotifications = () => api.get(NOTIF);

export const markNotificationRead = (id) => api.post(`${NOTIF}/${id}/read`);

export const markAllNotificationsRead = () => api.post(`${NOTIF}/read-all`);
